import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { Loader } from '@sm-dashboard/ui-components';
import { ChatTurn } from '@/api/ai/chat';
import { getToolLabel } from './toolLabels';
import { ChatUiMessage } from './useCrmAiChat';
import ChatActionCard from './ChatActionCard';

interface ChatMessageListProps {
  messages: ChatUiMessage[];
  isStreaming: boolean;
  activeTool: string | null;
  onApproveAction: (index: number) => void;
  onRejectAction: (index: number) => void;
}

const bubbleSx = (role: ChatTurn['role']) =>
  role === 'user'
    ? {
        alignSelf: 'flex-start',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        borderRadius: '16px 4px 16px 16px'
      }
    : {
        alignSelf: 'flex-end',
        bgcolor: 'grey.100',
        color: 'text.primary',
        borderRadius: '4px 16px 16px 16px'
      };

const ChatMessageList = ({ messages, isStreaming, activeTool, onApproveAction, onRejectAction }: ChatMessageListProps) => {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, activeTool]);

  const lastMessage = messages[messages.length - 1];
  const waitingForFirstText = isStreaming && lastMessage?.role === 'assistant' && !lastMessage.content;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2, py: 1.5 }}>
      {messages.map((message, index) =>
        message.action ? (
          <ChatActionCard
            key={index}
            action={message.action}
            disabled={isStreaming}
            onApprove={() => onApproveAction(index)}
            onReject={() => onRejectAction(index)}
          />
        ) : message.role === 'assistant' && !message.content ? null : (
          <Box
            key={index}
            sx={{
              ...bubbleSx(message.role),
              maxWidth: '85%',
              px: 1.5,
              py: 1,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 14,
              lineHeight: 1.5
            }}
          >
            {message.content}
          </Box>
        )
      )}

      {(waitingForFirstText || activeTool) && (
        <Box
          sx={{
            alignSelf: 'flex-end',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            color: 'text.secondary',
            fontSize: 13
          }}
        >
          <Loader size={14} />
          {activeTool ? getToolLabel(activeTool) : 'חושב...'}
        </Box>
      )}

      <Box ref={bottomRef} />
    </Box>
  );
};

export default ChatMessageList;
